import { useStore } from 'hooks/useStore';
import {
  allOptionsInterface,
  NDCFormArrayFieldInteface,
  NDCFormFields,
  NDCFormFieldsTypes,
} from 'Interfaces/FactsInterfaces/drugInterfaces';
import { useEffect, useState } from 'react';
import { deepCopy } from 'utils/deepCopy';
import {
  convertNdcFormDataToAllOptions,
  createNdcPopUpDataForIndividualEdit,
  modifyNdcPopUpDateForIndividualEdit,
  selectedOptionUtility,
} from '../../utils/utilities';

export const useNdcStrategyAddModal = () => {
  const {
    FactsStore: {
      selectedNdcRowData,
      ndcTablesData,
      isNcdTableLoading,
      handleFetchNdcHistory,
      isNdcHistoryLoading,
      ndcFormDropDownData,
      addModalVisibility,
      toggleAddModalVisibility,
    },
  } = useStore();
  const [ndcStrategyIndividualEditData, setNdcStrategyIndividualEditData] =
    useState<NDCFormFields | null>(
      selectedNdcRowData
        ? createNdcPopUpDataForIndividualEdit(
            modifyNdcPopUpDateForIndividualEdit(selectedNdcRowData),
            true
          )
        : null
    );
  const [initialNdcStrategyIndividualEditData, setInitialNdcStrategyIndividualEditData] =
    useState<NDCFormFields | null>(
      selectedNdcRowData
        ? createNdcPopUpDataForIndividualEdit(
            modifyNdcPopUpDateForIndividualEdit(selectedNdcRowData),
            true
          )
        : null
    );
  const [allOptions, setAllOptions] = useState<allOptionsInterface | null>({
    PPS: [],
    PPSSC: [],
    Segmentation: [],
    DPStrategy: [],
  });
  const [isNdcUpdating, _setIsNdcUpdating] = useState(false);
  const [isHistoryVisible, setIsHistoryVisible] = useState(false);
  const [errorDetails, _setErrorDetails] = useState({
    isError: false,
    errorDetails: '',
  });
  const [addModalHeading, setAddModalHeading] = useState<NDCFormFieldsTypes | null>(null);
  const [newCreatedOption, _setNewCreatedOption] = useState<string>('');
  const [isNewNdcAddVisible, setIsNewNdcAddVisible] = useState(false);
  const [newlyAddedFutureNdcData, setNewlyAddedFutureNdcData] = useState<
    { heading: string; value: string; startDate: string; endDate: string }[]
  >([]);

  const allDropdownOptions = () => {
    const tempFormFields: { name: string; code: NDCFormFieldsTypes }[] = [];
    if (ndcStrategyIndividualEditData) {
      for (const [key, value] of Object.entries(ndcStrategyIndividualEditData)) {
        if (!value.selected) {
          if (key === 'PPSSC') {
            // PPSSC handled with PPS
          } else if (key === 'PPS')
            tempFormFields.push({
              name: 'PPS Strategy & Reason',
              code: key as NDCFormFieldsTypes,
            });
          else {
            tempFormFields.push({
              name: value.fieldName,
              code: key as NDCFormFieldsTypes,
            });
          }
        }
      }
    }
    return tempFormFields;
  };

  const [allStrategyOptions, setAllStrategyOptions] = useState(allDropdownOptions());

  const maxDateFromGivenAndTodays = (givenDate: Date) => {
    const date = new Date();
    if (givenDate > date) {
      return givenDate;
    } else {
      return date;
    }
  };

  const formFieldsArrayConverter = () => {
    const tempFormFields: NDCFormArrayFieldInteface[] = [];
    if (ndcStrategyIndividualEditData) {
      for (const [key, value] of Object.entries(ndcStrategyIndividualEditData)) {
        tempFormFields.push({ keyName: key as NDCFormFieldsTypes, ...value });
      }
    }
    return tempFormFields;
  };

  const selectedOption = (type: NDCFormFieldsTypes, value: string) => {
    if (ndcFormDropDownData)
      return selectedOptionUtility(
        convertNdcFormDataToAllOptions(ndcFormDropDownData),
        type,
        value
      );
  };

  useEffect(() => {
    if (ndcFormDropDownData) {
      setAllOptions(convertNdcFormDataToAllOptions(ndcFormDropDownData));
    }
  }, [ndcFormDropDownData, newCreatedOption]);

  useEffect(() => {
    if (selectedNdcRowData) {
      setNdcStrategyIndividualEditData(
        createNdcPopUpDataForIndividualEdit(
          modifyNdcPopUpDateForIndividualEdit(selectedNdcRowData),
          true
        )
      );
      setInitialNdcStrategyIndividualEditData(
        createNdcPopUpDataForIndividualEdit(
          modifyNdcPopUpDateForIndividualEdit(selectedNdcRowData),
          true
        )
      );
    }
  }, [selectedNdcRowData, ndcTablesData]);

  useEffect(() => {
    handleFetchNdcHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial load only
  }, []);

  const toggleHistoryVisibility = () => {
    setIsHistoryVisible(!isHistoryVisible);
  };

  const toggleNewNdcPopVisibility = () => {
    setIsNewNdcAddVisible(!isNewNdcAddVisible);
    setNewlyAddedFutureNdcData([]);
  };

  const getSelectedOption = (value: string, fieldName: NDCFormFieldsTypes) => {
    let result = '';
    if (allOptions) {
      allOptions[fieldName].forEach(option => {
        if (option.code === value) result = option.code;
      });
    }
    return result;
  };

  const isAnyStrategySelected = () => {
    let flag = false;
    if (ndcStrategyIndividualEditData) {
      for (const [_key, value] of Object.entries(ndcStrategyIndividualEditData)) {
        if (value.selected) flag = true;
      }
    }
    return flag;
  };

  const handleStrategySelect = (strategyName: NDCFormFieldsTypes) => {
    if (ndcStrategyIndividualEditData && initialNdcStrategyIndividualEditData) {
      const tempNDCData = deepCopy(ndcStrategyIndividualEditData);
      tempNDCData[strategyName].selected = true;
      if (strategyName === 'PPS' || strategyName === 'PPSSC') {
        tempNDCData['PPS'].selected = true;
        tempNDCData['PPSSC'].selected = true;
      }
      setNdcStrategyIndividualEditData(tempNDCData);

      const tempInitialNDCData = deepCopy(initialNdcStrategyIndividualEditData);
      tempInitialNDCData[strategyName].selected = true;
      if (strategyName === 'PPS' || strategyName === 'PPSSC') {
        tempInitialNDCData['PPS'].selected = true;
        tempInitialNDCData['PPSSC'].selected = true;
      }
      setInitialNdcStrategyIndividualEditData(tempInitialNDCData);

      const tempFormFields: { name: string; code: NDCFormFieldsTypes }[] = [];
      for (const [key, value] of Object.entries(ndcStrategyIndividualEditData)) {
        if (!value.selected)
          tempFormFields.push({
            name: value.fieldName,
            code: key as NDCFormFieldsTypes,
          });
      }
      setAllStrategyOptions(tempFormFields);
    }
  };

  const handleUnselectStrategy = (strategyName: NDCFormFieldsTypes) => {
    if (ndcStrategyIndividualEditData && initialNdcStrategyIndividualEditData) {
      const tempNDCData = deepCopy(ndcStrategyIndividualEditData);
      tempNDCData[strategyName].selected = false;
      if (strategyName === 'PPS' || strategyName === 'PPSSC') {
        tempNDCData['PPS'].selected = false;
        tempNDCData['PPSSC'].selected = false;
      }
      setNdcStrategyIndividualEditData(tempNDCData);

      const tempInitialNDCData = deepCopy(initialNdcStrategyIndividualEditData);
      tempInitialNDCData[strategyName].selected = false;
      if (strategyName === 'PPS' || strategyName === 'PPSSC') {
        tempInitialNDCData['PPS'].selected = false;
        tempInitialNDCData['PPSSC'].selected = false;
      }
      setInitialNdcStrategyIndividualEditData(tempInitialNDCData);

      const tempFormFields: { name: string; code: NDCFormFieldsTypes }[] = [];
      for (const [key, value] of Object.entries(ndcStrategyIndividualEditData)) {
        if (!value.selected)
          tempFormFields.push({
            name: value.fieldName,
            code: key as NDCFormFieldsTypes,
          });
      }
      setAllStrategyOptions(tempFormFields);
    }
  };

  return {
    isNdcUpdating,
    isNcdTableLoading,
    allDropdownOptions,
    isAnyStrategySelected,
    handleStrategySelect,
    handleUnselectStrategy,
    selectedNdcRowData,
    errorDetails,
    ndcStrategyIndividualEditData,
    isNdcHistoryLoading,
    convertNdcFormDataToAllOptions,
    initialNdcStrategyIndividualEditData,
    maxDateFromGivenAndTodays,
    isHistoryVisible,
    toggleHistoryVisibility,
    formFieldsArrayConverter,
    selectedOption,
    allOptions,
    addModalVisibility,
    toggleAddModalVisibility,
    addModalHeading,
    setAddModalHeading,
    ndcFormDropDownData,
    getSelectedOption,
    isNewNdcAddVisible,
    newlyAddedFutureNdcData,
    toggleNewNdcPopVisibility,
    allStrategyOptions,
  };
};
